import * as React from 'react';
import { useUserNotesModal } from 'Hooks/useUserNotesModal';
import { useState } from 'react';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import alertToast from 'Util/ToastifyUtils';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import If from 'Views/Components/If/If';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { isNullOrUndefined } from 'Util/TypeGuards';
import moment from 'moment';
import { upperCaseFirst } from 'Util/StringUtils';
import { noteType } from 'Models/Enums';
import { addCustomStaffUserNote, noteInfoDto } from 'Services/Api/_HumanWritten/NotesService';

export interface UserNotesModalProps {
	userId: string;
	/** Change this to pull the notes again — see useUserNotesModal. */
	refreshToken?: unknown;
}

export function UserNotesModal({ userId, refreshToken }: UserNotesModalProps) {
	const response = useUserNotesModal(userId, refreshToken);
	// Closed on every screen size, not just mobile. The panel opens onto the full note history, which
	// on a customer with any history at all pushes the account itself off the screen — and the notes
	// are what staff go looking for, not what they arrive for.
	const [showNotes, setShowNotes] = useState<boolean>(false);
	const [noteInput, setNoteInput] = useState('');

	const createCustomStaffNote = async () => {
		if (userId != null && noteInput !== '') {
			const result = await addCustomStaffUserNote({
				noteContent: noteInput,
				userId: userId,
			});

			if (result === false) {
				alertToast('Could not add new note', 'error');
			} else {
				setNoteInput('');
				response.refresh();
			}
		}
	};

	if (!response || response.type === 'error') {
		alertToast('Failed to fetch notes for booking.', 'error');
		return <></>;
	}

	const notes = (response.data as noteInfoDto[]) ?? [];

	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		createCustomStaffNote();
	};

	return (
		<div className="notes-modal__container">
			<div
				key="notes-header"
				onKeyPress={() => {}}
				className="notes-modal__header"
				onClick={() => setShowNotes(!showNotes)}
			>
				<div className="left-container">
					<h6 className="title">Notes</h6>
					<h6 className="note-count">{notes?.length ?? 0}</h6>
				</div>
				<Button
					className={`show-hide-notes-panel-button ${
						showNotes ? 'icon-chevron-down' : 'icon-chevron-up'
					} icon-only`}
					colors={Colors.Secondary}
					display={Display.Text}
					onClick={() => setShowNotes(!showNotes)}
				/>
			</div>
			<If condition={showNotes}>
				<div className="past-notes__container">{renderNotes(notes)}</div>
				<div className="new-note__container">
					<form onSubmit={onSubmit}>
						<div className="new-note__input-wrapper custom-input">
							<textarea
								id="new_note-field"
								value={noteInput}
								onChange={e => setNoteInput(e.target.value)}
								autoComplete="off"
								placeholder="Write a note..."
								rows={2}
								className="new-note__textarea"
							/>
							<button
								type="submit"
								className={`new-note__send-btn ${noteInput ? 'active' : ''}`}
								disabled={!noteInput}
							>
								<Icon name="send" useMaterialIcons />
							</button>
						</div>
					</form>
					<p className="new-note__hint">Notes are visible to staff only.</p>
				</div>
			</If>
		</div>
	);
}

function renderNotes(notes: noteInfoDto[]) {
	const listOfNotes: JSX.Element[] = [];
	if (isNullOrUndefined(notes)) {
		return <></>;
	}
	const uniqueDates = notes.filter(
		(noteInfo, index, arr) =>
			arr.findIndex(
				t => new Date(t.creationDateTime).toDateString() === new Date(noteInfo.creationDateTime).toDateString(),
			) === index,
	);

	uniqueDates.forEach(x => {
		notes.forEach(noteInfo => {
			let backgroundNote = false;
			if (
				(noteInfo.content.includes('Started alteration') ||
					noteInfo.content.includes('check') ||
					noteInfo.content.includes('invoiced') ||
					noteInfo.content.includes('awaiting')) &&
				noteInfo.noteType !== 'STAFF'
			) {
				backgroundNote = true;
			}
			if (new Date(noteInfo.creationDateTime).toDateString() === new Date(x.creationDateTime).toDateString()) {
				if (backgroundNote) {
					listOfNotes.push(
						<div className="note__container" key={noteInfo.noteId}>
							<div className="note__header background_note">
								{`${noteInfo.userName} ${noteInfo.content.toLowerCase()} at ${formatStandardTime(
									noteInfo.creationDateTime,
								)}`}
							</div>
						</div>,
					);
				} else {
					listOfNotes.push(
						<div className="note__container" key={noteInfo.noteId}>
							<div className="note__header">
								<div className="note-author">
									<If condition={noteInfo.locationType === 'CHECK_IN'}>
										<div className="issue-icon grey-icon" />
									</If>
									<div>{noteInfo.userName}</div>
								</div>
								<p className="note-created-time">{formatStandardTime(noteInfo.creationDateTime)}</p>
							</div>
							<div className={`note__content ${backgroundNote ? 'expire-reservation-note' : ''}`}>
								{convertContentToParagraphs(noteInfo.content, noteInfo.noteType)}
							</div>
							<If condition={!backgroundNote}>
								<div className={`note__tag ${noteInfo.noteType.toLowerCase()}`}>
									{noteInfo.noteType === 'USER'
										? 'Customer'
										: upperCaseFirst(noteInfo.noteType.toLowerCase())}
								</div>
							</If>
						</div>,
					);
				}
			}
		});
		// Add the date header for the above notes (sorry it's backwards)
		listOfNotes.push(
			<div className="date-line" key={new Date(x.creationDateTime).toDateString()}>
				<p className="date-header">{moment(x.creationDateTime).format('DD MMM YYYY').toUpperCase()}</p>
			</div>,
		);
	});

	return listOfNotes;
}

/**
 * Renders a note's content in the same shape the booking notes sidebar uses for a booking update: a
 * small grey label above each value, a section heading with a rule under it between blocks, and the
 * first line as a plain title.
 *
 * Structured formatting is applied to system-written notes only. A STAFF or USER note is somebody's
 * free text, and a sentence like "Called customer: no answer" would otherwise be split into a label
 * and a value — so those stay as plain paragraphs, exactly as they were.
 *
 * Three line shapes are recognised:
 *   - an ALL-CAPS line with no colon is a section heading
 *   - `Label: value` becomes a label above its value
 *   - anything else is a plain line
 *
 * Lines under a REASON heading are always plain: the text is whatever a staff member typed, and a
 * colon inside it is punctuation rather than a label.
 */
const convertContentToParagraphs = (content: string, typeOfNote: noteType) => {
	const isFreeText = typeOfNote === 'STAFF' || typeOfNote === 'USER';
	const listOfStrings = content.split('\n');
	const listOfParagraphs: JSX.Element[] = [];
	let count = 0;
	let inReason = false;

	listOfStrings.forEach(paragraph => {
		if (paragraph === '') {
			return;
		}
		count++;

		if (isFreeText) {
			listOfParagraphs.push(<p key={count}>{paragraph}</p>);
			return;
		}

		const isHeading = !paragraph.includes(':') && paragraph === paragraph.toUpperCase()
			&& /[A-Z]/.test(paragraph);

		if (isHeading) {
			inReason = paragraph === 'REASON';
			// Heading plus rule, the same pairing the booking note draws under "Created booking".
			listOfParagraphs.push(
				<div key={count} className="note-paragraph">
					<p className="note-header">{paragraph}</p>
					<p>___________________</p>
				</div>,
			);
			return;
		}

		const separator = paragraph.indexOf(':');
		if (inReason || separator < 1) {
			listOfParagraphs.push(<p key={count}>{paragraph}</p>);
			return;
		}

		listOfParagraphs.push(
			<div key={count} className="note-paragraph">
				<p className="note-header">{paragraph.slice(0, separator)}</p>
				<p>{paragraph.slice(separator + 1).trim()}</p>
			</div>,
		);
	});

	return listOfParagraphs;
};
